'use client';

import { useState, useEffect } from 'react';
import { useCounter } from '@/components/marketing/engine/use-counter';

/* ───────────── VAULT — proctored assessment, live integrity ─────────────
   Code editor + biometric face-mesh camera + trust score + proctoring grid.
   Grounded in the live Vault proctored-session screen. */

interface Signal {
  k: string;
  v: string;
}

export function VaultExp() {
  const [step, setStep] = useState<number>(0);

  useEffect(() => {
    const ms = [350, 1050, 1750, 2450, 3150];
    const t = ms.map((m, i) => setTimeout(() => setStep(i + 1), m));
    return () => t.forEach(clearTimeout);
  }, []);

  const trust = Number(useCounter(97, 1500, step >= 2, 0));

  const SIGNALS: Signal[] = [
    { k: 'Face match', v: '99.4%' },
    { k: 'Gaze on-screen', v: '97.1%' },
    { k: 'Audio env.', v: 'Clean' },
    { k: 'Device', v: 'Locked' },
  ];

  const band = trust >= 85 ? 'var(--green)' : trust >= 70 ? 'var(--indigo-light)' : 'var(--crimson)';
  const faceLocked = step >= 1;

  return (
    <div className="exp-split" style={{ display: 'grid', gridTemplateColumns: '1fr 232px', gap: '14px', height: '100%' }}>
      {/* LEFT — code editor */}
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
          <span className="mono" style={{ fontSize: '10px', color: 'var(--text-3)' }}>{'</>'} solution.py</span>
          <span className="mono" style={{ fontSize: '9px', color: 'var(--text-3)' }}>ARRAYS · MEDIUM · Q7/12</span>
        </div>
        <div style={{ padding: '14px 16px', flex: 1, fontFamily: 'var(--mono)', fontSize: '12px', lineHeight: 1.85 }}>
          <div><span style={{ color: 'var(--text-4)', marginRight: '14px' }}>1</span><span style={{ color: 'var(--indigo-light)' }}>class</span> <span style={{ color: 'var(--text)' }}>Solution</span><span style={{ color: 'var(--text-2)' }}>:</span></div>
          <div><span style={{ color: 'var(--text-4)', marginRight: '14px' }}>2</span><span style={{ color: 'var(--text-3)' }}>{'  '}</span><span style={{ color: 'var(--indigo-light)' }}>def</span> <span style={{ color: 'var(--green)' }}>twoSum</span><span style={{ color: 'var(--text-2)' }}>(self, nums, target):</span></div>
          <div><span style={{ color: 'var(--text-4)', marginRight: '14px' }}>3</span><span style={{ color: 'var(--text-3)' }}>{'    '}</span><span style={{ color: 'var(--text-2)' }}>seen = {'{}'}</span></div>
          <div><span style={{ color: 'var(--text-4)', marginRight: '14px' }}>4</span><span style={{ color: 'var(--text-3)' }}>{'    '}</span><span style={{ color: 'var(--indigo-light)' }}>for</span> <span style={{ color: 'var(--text-2)' }}>i, n </span><span style={{ color: 'var(--indigo-light)' }}>in</span> <span style={{ color: 'var(--text-2)' }}>enumerate(nums):</span></div>
          <div><span style={{ color: 'var(--text-4)', marginRight: '14px' }}>5</span><span style={{ color: 'var(--text-3)' }}>{'      '}</span><span style={{ color: 'var(--indigo-light)' }}>if</span> <span style={{ color: 'var(--text-2)' }}>target - n </span><span style={{ color: 'var(--indigo-light)' }}>in</span> <span style={{ color: 'var(--text-2)' }}>seen:</span></div>
          <div><span style={{ color: 'var(--text-4)', marginRight: '14px' }}>6</span><span style={{ color: 'var(--text-3)' }}>{'        '}</span><span style={{ color: 'var(--indigo-light)' }}>return</span> <span style={{ color: 'var(--text-2)' }}>[seen[target - n], i]</span><span style={{ display: 'inline-block', width: '7px', height: '14px', background: 'var(--green)', marginLeft: '2px', verticalAlign: 'middle', animation: 'blink 1.1s step-end infinite' }}></span></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', borderTop: '1px solid var(--border)' }}>
          <span className="mono" style={{ fontSize: '9px', color: 'var(--text-3)' }}>autosaved · keystrokes recorded</span>
          <span className="mono" style={{ fontSize: '10px', color: 'var(--text-2)' }}>24:15</span>
        </div>
      </div>

      {/* RIGHT — camera + proctoring */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
        {/* biometric camera tile */}
        <div style={{ position: 'relative', height: '116px', borderRadius: 'var(--r-md)', overflow: 'hidden', border: '1px solid var(--border)',
          background: 'radial-gradient(120% 120% at 50% 30%, var(--surface-raised), var(--surface))' }}>
          <FaceMesh active={faceLocked} />
          <span className="mono" style={{ position: 'absolute', top: '8px', left: '9px', fontSize: '8px', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--green)', animation: 'spill-pulse 1.4s ease infinite' }}></span>CAMERA
          </span>
          <span className="mono" style={{ position: 'absolute', top: '8px', right: '9px', fontSize: '8px', fontWeight: 600,
            color: faceLocked ? 'var(--green)' : 'var(--text-3)', transition: 'color .5s' }}>{faceLocked ? 'FACE OK' : 'locking…'}</span>
        </div>

        {/* proctoring card */}
        <div style={{ flex: 1, borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--surface)', padding: '13px 14px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span className="label" style={{ fontSize: '8px' }}>PROCTORING</span>
            <span className="mono" style={{ fontSize: '9px', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Clean · 0
            </span>
          </div>

          {/* signal grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px', marginBottom: '14px' }}>
            {SIGNALS.map((s, i) => {
              const ok = step > i;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 9px', borderRadius: 'var(--r-sm)',
                  background: ok ? 'var(--green-soft)' : 'var(--surface-raised)', border: `1px solid ${ok ? 'color-mix(in srgb, var(--green) 20%, transparent)' : 'var(--border)'}`,
                  transition: 'all .5s var(--ease)', minWidth: 0 }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: ok ? 'var(--green)' : 'var(--text-4)', transition: 'background .4s' }}></span>
                  <div style={{ minWidth: 0, lineHeight: 1.25 }}>
                    <div style={{ fontSize: '9.5px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.k}</div>
                    <div className="mono" style={{ fontSize: '9px', fontWeight: 600, color: ok ? 'var(--green)' : 'var(--text-4)', transition: 'color .4s' }}>{ok ? s.v : '·'}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* trust score */}
          <div style={{ marginTop: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span className="label" style={{ fontSize: '8px' }}>TRUST SCORE</span>
              <span className="mono" style={{ fontSize: '13px', fontWeight: 700, color: band, transition: 'color .4s' }}>{step >= 2 ? `${trust}%` : '—'}</span>
            </div>
            <div style={{ height: '5px', borderRadius: '3px', background: 'var(--surface-raised)', overflow: 'hidden' }}>
              <div style={{ width: step >= 2 ? `${trust}%` : '0%', height: '100%', background: band, borderRadius: '3px', transition: 'width .3s linear, background .4s' }}></div>
            </div>
            <div className="mono" style={{ fontSize: '8px', color: 'var(--text-3)', marginTop: '7px' }}>
              {step >= 4 ? 'session integrity verified · signed' : 'evaluating session…'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* stylized biometric face-mesh — no photo, landmark dots + scan sweep */
function FaceMesh({ active }: { active: boolean }) {
  const dots = [
    [60, 30], [44, 42], [76, 42], [60, 46], [40, 58], [80, 58],
    [52, 62], [68, 62], [60, 70], [50, 80], [70, 80], [60, 86],
    [34, 50], [86, 50], [60, 56],
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="100%" height="100%" viewBox="0 0 120 116" preserveAspectRatio="xMidYMid meet" style={{ opacity: active ? 1 : 0.45, transition: 'opacity .6s' }}>
        {/* head contour */}
        <path d="M60 14 C40 14 30 30 30 50 C30 74 44 96 60 100 C76 96 90 74 90 50 C90 30 80 14 60 14 Z"
          fill="none" stroke="var(--indigo-light)" strokeWidth="0.8" opacity="0.5" />
        {/* mesh contours */}
        <path d="M34 50 Q60 40 86 50" fill="none" stroke="var(--indigo-light)" strokeWidth="0.5" opacity="0.4" />
        <path d="M40 64 Q60 72 80 64" fill="none" stroke="var(--indigo-light)" strokeWidth="0.5" opacity="0.4" />
        <path d="M60 30 L60 86" fill="none" stroke="var(--indigo-light)" strokeWidth="0.5" opacity="0.3" />
        <path d="M44 42 L52 62 L60 70 L68 62 L76 42" fill="none" stroke="var(--indigo-light)" strokeWidth="0.5" opacity="0.4" />
        {/* landmark dots */}
        {dots.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.4" fill={active ? 'var(--green)' : 'var(--indigo-light)'}
            style={{ transition: 'fill .6s', animation: active ? `node-pulse ${1.6 + (i % 4) * 0.25}s ease-in-out infinite` : 'none' }} />
        ))}
        {/* scan sweep */}
        {active && (
          <line x1="26" y1="0" x2="94" y2="0" stroke="var(--green)" strokeWidth="1.2" opacity="0.7" style={{ animation: 'vault-scan 2.6s var(--ease) infinite' }} />
        )}
      </svg>
      <style>{`@keyframes vault-scan { 0% { transform: translateY(14px); opacity: 0; } 18% { opacity: .8; } 82% { opacity: .8; } 100% { transform: translateY(100px); opacity: 0; } }`}</style>
    </div>
  );
}
